export type UnitSystem = "metric" | "imperial";

export const G_PER_OZ = 28.349523125;
export const G_PER_LB = 453.59237;

// `digits` is the call site's existing metric precision. Omit it to keep the
// metric value un-rounded (String(g)), exactly as a bare `${g}g` rendered before.
function metricNum(n: number, digits?: number): string {
  return digits === undefined ? String(n) : n.toFixed(digits);
}

/** Weight in grams -> display string. Imperial: < 1 lb "x.x oz", else "x.xx lb". */
export function fmtWeight(g: number, system: UnitSystem, digits?: number): string {
  if (system === "imperial") {
    return Math.abs(g) >= G_PER_LB ? `${(g / G_PER_LB).toFixed(2)} lb` : `${(g / G_PER_OZ).toFixed(1)} oz`;
  }
  return `${metricNum(g, digits)}g`;
}

/** Rate in g/min -> display string. Imperial: oz/hr, 1 dp. */
export function fmtRate(gpm: number, system: UnitSystem, digits?: number): string {
  const { value, unit } = rateParts(gpm, system, digits);
  return `${value} ${unit}`;
}

/** Number and unit separately, for layouts that style them apart (headers, stat tiles). */
export function rateParts(gpm: number, system: UnitSystem, digits?: number): { value: string; unit: string } {
  if (system === "imperial") return { value: ((gpm * 60) / G_PER_OZ).toFixed(1), unit: "oz/hr" };
  return { value: metricNum(gpm, digits), unit: "g/min" };
}

/** Bare weight number for columns whose header carries the unit ("Total g"). Imperial is always ounces. */
export function weightParts(g: number, system: UnitSystem, digits?: number): { value: string; unit: string } {
  if (system === "imperial") return { value: (g / G_PER_OZ).toFixed(1), unit: "oz" };
  return { value: metricNum(g, digits), unit: "g" };
}

/** Weight-entry text -> grams (imperial input is ounces). */
export function parseWeightInput(value: string | number, system: UnitSystem): number {
  const n = typeof value === "number" ? value : Number(value);
  return system === "imperial" ? n * G_PER_OZ : n;
}

/** Grams -> entry text in the org's unit (for pre-filling edit inputs). */
export function weightToInput(g: number, system: UnitSystem): string {
  return system === "imperial" ? String(Math.round((g / G_PER_OZ) * 100) / 100) : String(g);
}

/** Word for entry labels/placeholders: "grams" or "oz". */
export function weightEntryUnit(system: UnitSystem): string {
  return system === "imperial" ? "oz" : "grams";
}
